import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Tooltip,
  message,
  Drawer,
  Switch,
  Badge,
  Tag,
  Spin,
} from "antd";
import {
  SyncOutlined,
  SettingOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
  DatabaseOutlined,
  FileOutlined,
  GlobalOutlined,
  DesktopOutlined,
  CheckSquareOutlined,
  AppstoreOutlined,
  RobotOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  RiseOutlined,
  FallOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";
import { useUser } from "../context/UserContext";
import DashboardWidget from "../components/dashboard/DashboardWidget";
import StatStrip from "../components/dashboard/StatStrip";
import MiniSparkline from "../components/dashboard/MiniSparkline";
import RunStatusBadge from "../components/dashboard/RunStatusBadge";
import {
  getDashboardSummary,
  getDashboardTrend,
  getDashboardLibrary,
  getDashboardRecentRuns,
  getDashboardMasterSuiteHealth,
  getDashboardJobBreakdown,
  getDashboardFailureHeatmap,
  getDashboardWidgetConfig,
  saveDashboardWidgetConfig,
} from "../service/dashboard.service";
import "../styles/dashboard.css";

// ─── Default widget config ────────────────────────────────────
const DEFAULT_WIDGETS = [
  { id: "kpi", label: "KPI strip", enabled: true, size: "full" },
  { id: "trend", label: "Pass rate trend", enabled: true, size: "large" },
  { id: "breakdown", label: "Job breakdown", enabled: true, size: "small" },
  { id: "runs", label: "Recent runs", enabled: true, size: "large" },
  { id: "health", label: "Master suite health", enabled: true, size: "large" },
  { id: "library", label: "Library snapshot", enabled: true, size: "full" },
  { id: "heatmap", label: "Failure heatmap", enabled: true, size: "full" },
  { id: "sql", label: "SQL Agent (soon)", enabled: true, size: "third" },
  { id: "brd", label: "BRD Generator (soon)", enabled: true, size: "third" },
  { id: "agents", label: "Local Agents (soon)", enabled: true, size: "third" },
];

// ─── Chart theme (works light + dark) ────────────────────────
const CHART_THEME = {
  fontFamily: "inherit",
  background: "transparent",
  toolbar: { show: false },
};
const GRID_STYLE = {
  borderColor: "rgba(128,128,128,0.15)",
  strokeDashArray: 3,
  xaxis: { lines: { show: false } },
};
const TOOLTIP_THEME = { theme: "dark" };
const AXIS_STYLE = {
  labels: {
    style: { fontSize: "11px", cssClass: "apexcharts-axis-label" },
  },
  axisBorder: { show: false },
  axisTicks: { show: false },
};

// ─── Colours ─────────────────────────────────────────────────
const C = {
  pass: "#3B6D11",
  fail: "#A32D2D",
  blue: "#185FA5",
  amber: "#854F0B",
  purple: "#534AB7",
  gray: "#5F5E5A",
  passBg: "#EAF3DE",
  failBg: "#FCEBEB",
  blueBg: "#E6F1FB",
  amberBg: "#FAEEDA",
  purpleBg: "#EEEDFE",
  grayBg: "#F1EFE8",
};

// Normalized master-suite health status (live status wins over last result)
const msStatus = (ms) =>
  ms.last_run_status === "In Progress"
    ? "In Progress"
    : ms.last_result === "Pass"
      ? "Pass"
      : ms.last_result === "Fail"
        ? "Fail"
        : "No runs";

const MS_FILTERS = [
  { label: "All", value: "all" },
  { label: "In progress", value: "In Progress" },
  { label: "Passed", value: "Pass" },
  { label: "Failed", value: "Fail" },
];

// ─── Helpers ─────────────────────────────────────────────────
function greet() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function fmtRows(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function dayLabel(dateStr) {
  if (!dateStr) return "";
  const p = dateStr.split("-");
  if (p.length < 3) return dateStr;
  try {
    return new Date(`${p[2]}-${p[1]}-${p[0]}`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return dateStr;
  }
}

function catMeta(cat, sub) {
  const c = (cat || "").toLowerCase(),
    s = (sub || "").toLowerCase();
  if (s.includes("ui"))
    return { color: C.purple, bg: C.purpleBg, icon: <DesktopOutlined /> };
  if (c.includes("compare"))
    return { color: C.blue, bg: C.blueBg, icon: <FileOutlined /> };
  if (c.includes("process"))
    return { color: C.amber, bg: C.amberBg, icon: <GlobalOutlined /> };
  if (c.includes("master"))
    return { color: C.pass, bg: C.passBg, icon: <CheckSquareOutlined /> };
  return { color: C.gray, bg: C.grayBg, icon: <AppstoreOutlined /> };
}

// ─── Period tabs ──────────────────────────────────────────────
function PeriodTabs({ value, onChange, options }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            padding: "3px 10px",
            borderRadius: 20,
            fontSize: 12,
            cursor: "pointer",
            border: "0.5px solid",
            borderColor:
              value === o.value ? "#0B1F3A" : "var(--color-border-tertiary)",
            background: value === o.value ? "#0B1F3A" : "transparent",
            color: value === o.value ? "#fff" : "var(--color-text-secondary)",
            fontFamily: "inherit",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  delta,
  deltaLabel,
  color,
  sparkData,
  sparkColor,
  loading,
}) {
  // Backend returns null for a delta when the prior period is empty (no divide-by-zero
  // fake number) — hide the arrow/% entirely in that case.
  const hasDelta = delta !== undefined && delta !== null;
  const up = delta >= 0;
  return (
    <div
      style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderTop: `3px solid ${color}`,
        borderRadius: 12,
        padding: "16px 18px",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
        transition: "box-shadow .15s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)")
      }
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--color-text-secondary)",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      {loading ? (
        <Spin size="small" style={{ marginTop: 8 }} />
      ) : (
        <>
          <div
            style={{ fontSize: 28, fontWeight: 500, color, lineHeight: 1.15 }}
          >
            {value}
          </div>
          {hasDelta && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
              }}
            >
              {up ? (
                <ArrowUpOutlined style={{ color: C.pass, fontSize: 11 }} />
              ) : (
                <ArrowDownOutlined style={{ color: C.fail, fontSize: 11 }} />
              )}
              <span style={{ color: up ? C.pass : C.fail, fontWeight: 500 }}>
                {Math.abs(delta)}%
              </span>
              <span style={{ color: "var(--color-text-secondary)" }}>
                {deltaLabel}
              </span>
            </div>
          )}
          {sparkData?.length > 0 && (
            <MiniSparkline
              data={sparkData}
              color={sparkColor || color}
              height={32}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Master suite health card ─────────────────────────────────
function MsHealthCard({ ms, onClick }) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    // Delay chart render by one frame to avoid ApexCharts runMaskReveal crash
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const total = Number(ms.total_steps) || 0;
  const passed = Number(ms.passed_steps) || 0;
  const failed = Math.max(0, total - passed);
  const pct = total > 0 ? Math.round((passed / total) * 100) : 0;
  const inProgress = ms.last_run_status === "In Progress";
  const isPass = !inProgress && ms.last_result === "Pass";
  const isFail = !inProgress && ms.last_result === "Fail";
  const noRuns =
    !inProgress &&
    (!ms.last_result ||
      ms.last_result === "Never run" ||
      ms.last_result === "No runs");
  const gaugeColor = pct === 100 ? C.pass : pct > 60 ? "#EF9F27" : C.fail;
  const statusColor = inProgress
    ? C.amber
    : isPass
      ? C.pass
      : isFail
        ? C.fail
        : C.gray;
  const statusBg = inProgress
    ? C.amberBg
    : isPass
      ? C.passBg
      : isFail
        ? C.failBg
        : C.grayBg;

  const radialOpts = {
    chart: {
      type: "radialBar",
      background: "transparent",
      fontFamily: "inherit",
      toolbar: { show: false },
      animations: { enabled: false }, // disable to prevent runMaskReveal crash
    },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: { size: "55%" },
        dataLabels: {
          name: { show: false },
          value: {
            show: true,
            fontSize: "13px",
            fontWeight: 500,
            offsetY: 5,
            formatter: (v) => `${Math.round(v)}%`,
            color: "var(--color-text-primary)",
          },
        },
        track: {
          background: "var(--color-background-secondary)",
          strokeWidth: "100%",
        },
      },
    },
    colors: [gaugeColor],
    stroke: { lineCap: "round" },
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        transition: "border-color .15s, box-shadow .15s",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#0B1F3A";
        e.currentTarget.style.boxShadow = "0 2px 12px rgba(11,31,58,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--color-border-tertiary)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Tooltip title={ms.ms_name}>{ms.ms_name}</Tooltip>
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--color-text-secondary)",
              marginTop: 2,
            }}
          >
            Last run: {ms.last_run || "—"}
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 20,
            fontWeight: 500,
            background: statusBg,
            color: statusColor,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {inProgress ? (
            <>
              <SyncOutlined spin style={{ marginRight: 4 }} />
              Running
            </>
          ) : noRuns ? (
            "No runs"
          ) : isPass ? (
            "✓ Passed"
          ) : (
            "✗ Failed"
          )}
        </span>
      </div>

      {/* Gauge + stats */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            flexShrink: 0,
            width: 80,
            height: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {mounted ? (
            <ReactApexChart
              type="radialBar"
              options={radialOpts}
              series={[pct]}
              height={80}
              width={80}
            />
          ) : (
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                border: `3px solid ${gaugeColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 500,
                color: gaugeColor,
              }}
            >
              {pct}%
            </div>
          )}
        </div>
        <div
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
            }}
          >
            <span style={{ color: "var(--color-text-secondary)" }}>Steps</span>
            <span style={{ fontWeight: 500 }}>{total}</span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
            }}
          >
            <span style={{ color: C.pass }}>✓ Passed</span>
            <span style={{ fontWeight: 500, color: C.pass }}>{passed}</span>
          </div>
          {failed > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
              }}
            >
              <span style={{ color: C.fail }}>✗ Failed</span>
              <span style={{ fontWeight: 500, color: C.fail }}>{failed}</span>
            </div>
          )}
        </div>
      </div>

      {/* Pass bar */}
      <div
        style={{
          height: 5,
          borderRadius: 3,
          background: "var(--color-background-secondary)",
          overflow: "hidden",
        }}
      >
        {passed > 0 && (
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: gaugeColor,
              borderRadius: 3,
              transition: "width .4s",
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────
function Dashboard() {
  const { email, name } = useUser();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  // True when the summary request itself failed — lets the tiles say "couldn't load"
  // instead of silently showing the same "—" as a day with no runs.
  const [sumError, setSumError] = useState(false);
  const [trend, setTrend] = useState([]);
  const [library, setLibrary] = useState({});
  const [runs, setRuns] = useState([]);
  const [health, setHealth] = useState([]);
  const [msFilter, setMsFilter] = useState("all");
  const [breakdown, setBreakdown] = useState({});
  const [heatmap, setHeatmap] = useState([]);
  const [sparkData, setSparkData] = useState({});
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [configOpen, setConfigOpen] = useState(false);
  const [trendDays, setTrendDays] = useState(30);
  const [refreshKey, setRefreshKey] = useState(0);

  const [ldSum, setLdSum] = useState(true);
  const [ldTrnd, setLdTrnd] = useState(true);
  const [ldRuns, setLdRuns] = useState(true);
  const [ldHlth, setLdHlth] = useState(true);

  const on = (id) => widgets.find((w) => w.id === id)?.enabled !== false;
  const displayName = name?.split(" ")[0] || email?.split("@")[0] || "there";

  // ── Load widget config ──────────────────────────────────────
  useEffect(() => {
    if (!email) return;
    getDashboardWidgetConfig(email)
      .then((res) => {
        if (res.data?.widgetConfig) {
          try {
            setWidgets(JSON.parse(res.data.widgetConfig));
          } catch (_) {}
        }
      })
      .catch(() => {});
  }, [email]);

  // ── Load all data ───────────────────────────────────────────
  const loadAll = useCallback(() => {
    if (!email) return;

    setLdSum(true);
    setSumError(false);
    getDashboardSummary(email)
      .then((r) => {
        setSummary(r.data);
        setLdSum(false);
      })
      // Without this flag a failed request left summary=null and the tiles rendered
      // "—", which looks identical to a genuine no-runs-today day.
      .catch(() => {
        setSumError(true);
        setLdSum(false);
      });

    getDashboardLibrary()
      .then((r) => setLibrary(r.data || {}))
      .catch(() => {});

    setLdRuns(true);
    getDashboardRecentRuns(email, 8)
      .then((r) => {
        setRuns(r.data || []);
        setLdRuns(false);
      })
      .catch(() => setLdRuns(false));

    setLdHlth(true);
    getDashboardMasterSuiteHealth(email)
      .then((r) => {
        setHealth(r.data || []);
        setLdHlth(false);
      })
      .catch(() => setLdHlth(false));

    getDashboardJobBreakdown(email, 7)
      .then((r) => setBreakdown(r.data || {}))
      .catch(() => {});
    getDashboardFailureHeatmap(email, 28)
      .then((r) => setHeatmap(r.data || []))
      .catch(() => {});

    getDashboardTrend(30)
      .then((r) => {
        const d = r.data || [];
        setSparkData({
          jobs: d.slice(-14).map((x) => x.total),
          pass: d
            .slice(-14)
            .map((x) =>
              x.total > 0 ? Math.round((x.passed * 100) / x.total) : 0,
            ),
          rows: d.slice(-14).map((x) => x.rowsCompared || 0),
          fails: d.slice(-14).map((x) => x.failed),
        });
      })
      .catch(() => {});
  }, [email, refreshKey]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Trend when period changes ───────────────────────────────
  useEffect(() => {
    if (!email) return;
    setLdTrnd(true);
    getDashboardTrend(trendDays)
      .then((r) => {
        setTrend(r.data || []);
        setLdTrnd(false);
      })
      .catch(() => setLdTrnd(false));
  }, [email, trendDays]);

  // ── Save config ─────────────────────────────────────────────
  const saveConfig = () => {
    saveDashboardWidgetConfig(email, widgets)
      .then(() => {
        message.success("Dashboard layout saved");
        setConfigOpen(false);
      })
      .catch(() => message.error("Failed to save layout"));
  };

  // ── Trend chart ─────────────────────────────────────────────
  const trendLabels = trend.map((d) => dayLabel(d.runDate));
  const trendOptions = {
    chart: {
      ...CHART_THEME,
      type: "area",
      id: "trend",
      animations: { enabled: false },
    },
    colors: [C.pass, C.fail],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.4,
        opacityFrom: 0.35,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: trendLabels,
      ...AXIS_STYLE,
      tickAmount: Math.min(
        trendLabels.length,
        trendDays === 7 ? 7 : trendDays === 30 ? 10 : 15,
      ),
    },
    yaxis: {
      ...AXIS_STYLE,
      title: {
        text: "Jobs",
        style: {
          fontSize: "11px",
          color: "var(--color-text-secondary)",
          fontFamily: "inherit",
        },
      },
      min: 0,
    },
    grid: GRID_STYLE,
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      fontFamily: "inherit",
      markers: { width: 10, height: 10, radius: 2 },
      labels: { colors: "var(--color-text-primary)" },
    },
    tooltip: {
      ...TOOLTIP_THEME,
      x: { format: "dd MMM" },
      y: { formatter: (v) => `${v} jobs` },
    },
    dataLabels: { enabled: false },
    markers: { size: 0, hover: { size: 4 } },
  };
  const trendSeries = [
    { name: "Passed", data: trend.map((d) => d.passed) },
    { name: "Failed", data: trend.map((d) => d.failed) },
  ];

  // ── Breakdown chart ─────────────────────────────────────────
  const bkLabels = Object.keys(breakdown);
  const bkValues = Object.values(breakdown);
  const bkTotal = bkValues.reduce((a, b) => a + b, 0);
  // Colour by run-type NAME (stable regardless of slice count/order); fall back for
  // any unmapped type. Keys match the backend getJobBreakdown friendly-type CASE.
  const BK_TYPE_COLORS = {
    Compare: C.blue,
    "File to Spec": C.pass,
    "Master Suite": C.purple,
    "UI Automation": C.amber,
    "Process Automation": "#6C8A9B", // brand slate (--arc-table)
  };
  const bkColors = bkLabels.map((l) => BK_TYPE_COLORS[l] || C.gray);
  const bkOptions = {
    chart: { ...CHART_THEME, type: "pie", animations: { enabled: false } },
    colors: bkColors,
    labels: bkLabels,
    legend: {
      show: true,
      position: "bottom",
      fontSize: "12px",
      fontFamily: "inherit",
      markers: { width: 10, height: 10, radius: 2 },
      labels: { colors: "var(--color-text-primary)" },
      formatter: (seriesName, opts) => {
        const total = bkTotal || 1;
        const pct = Math.round((bkValues[opts.seriesIndex] / total) * 100);
        return `${seriesName} (${pct}%)`;
      },
    },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    tooltip: { ...TOOLTIP_THEME, y: { formatter: (v) => `${v} jobs` } },
  };

  // ── Heatmap colour ──────────────────────────────────────────
  const heatColor = (f) => {
    if (!f || f === 0) return "var(--color-background-secondary)";
    if (f <= 2) return "#C0DD97";
    if (f <= 5) return "#97C459";
    if (f <= 10) return "#639922";
    return "#3B6D11";
  };

  // ── Pass rate calculation ────────────────────────────────────
  const passRateToday = summary?.passRateToday ?? 0;

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div style={{ padding: "16px 24px 2rem", minHeight: "100vh" }}>
      {/* ── Page header ──────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: "1rem",
          borderBottom: "0.5px solid var(--color-border-tertiary)",
          marginBottom: "1.25rem",
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 500 }}>
            {greet()}, {displayName} 👋
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary)",
              marginTop: 2,
            }}
          >
            {new Date().toLocaleDateString("en-AU", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
            <span
              style={{
                marginLeft: 10,
                padding: "1px 8px",
                borderRadius: 20,
                background: C.blueBg,
                color: C.blue,
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              ARC Cloud
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<SettingOutlined />}
            onClick={() => setConfigOpen(true)}
          >
            Customise
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            style={{ background: "#0B1F3A", borderColor: "#0B1F3A" }}
            onClick={() => navigate("/mastersuite")}
          >
            Run a suite
          </Button>
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────── */}
      {on("kpi") && (
        <>
        {/* Make "request failed" and "nothing ran today" tell themselves apart —
            both used to render as a row of dashes. */}
        {!ldSum && sumError && (
          <div className="dash-kpi-note dash-kpi-note--error">
            Couldn't load today's figures. Check your connection and refresh.
          </div>
        )}
        {!ldSum && !sumError && summary && summary.jobsToday === 0 && (
          <div className="dash-kpi-note">
            No runs today yet — these figures cover today only.
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: "1.25rem",
            flexWrap: "wrap",
          }}
        >
          <KpiCard
            label="Jobs run today"
            value={summary?.jobsToday ?? "—"}
            color={C.blue}
            loading={ldSum}
            sparkData={sparkData.jobs}
            sparkColor={C.blue}
            deltaLabel="vs yesterday"
            delta={summary?.jobsTodayDelta}
          />
          <KpiCard
            label="Pass rate today"
            value={summary ? `${summary.passRateToday}%` : "—"}
            color={summary?.passRateToday >= 80 ? C.pass : C.fail}
            loading={ldSum}
            sparkData={sparkData.pass}
            sparkColor={C.pass}
            deltaLabel="vs yesterday"
            delta={summary?.passRateTodayDelta}
          />
          {/* Value is this month's rows so it matches its own month-over-month delta —
              previously it showed TODAY's rows against a "vs last month" delta. */}
          <KpiCard
            label="Rows compared (month)"
            value={summary?.rowsThisMonth ? fmtRows(summary.rowsThisMonth) : "—"}
            color={C.amber}
            loading={ldSum}
            sparkData={sparkData.rows}
            sparkColor={C.amber}
            deltaLabel="vs last month"
            delta={summary?.rowsThisMonthDelta}
          />
          <KpiCard
            label="Failures to fix"
            value={summary?.failedToday ?? "—"}
            color={summary?.failedToday > 0 ? C.fail : C.gray}
            loading={ldSum}
            sparkData={sparkData.fails}
            sparkColor={C.fail}
            deltaLabel="vs yesterday"
            delta={summary?.failedTodayDelta}
          />
          <KpiCard
            label="This week's jobs"
            value={summary?.jobsThisWeek ?? "—"}
            color={C.purple}
            loading={ldSum}
            sparkData={sparkData.jobs}
            sparkColor={C.purple}
            deltaLabel="vs last week"
            delta={summary?.jobsWeekDelta}
          />
        </div>
        </>
      )}

      {/* ── Pass rate trend + Job breakdown ───────────────── */}
      {(on("trend") || on("breakdown")) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 340px",
            gap: 12,
            marginBottom: "1.25rem",
          }}
        >
          {on("trend") && (
            <DashboardWidget
              title="Pass rate trend"
              subtitle={`Passed vs failed · last ${trendDays} days`}
              loading={ldTrnd}
              accent="#185FA5"
              actions={
                <PeriodTabs
                  value={trendDays}
                  onChange={setTrendDays}
                  options={[
                    { label: "7d", value: 7 },
                    { label: "30d", value: 30 },
                    { label: "90d", value: 90 },
                  ]}
                />
              }
            >
              {trend.length > 0 && trendSeries[0].data.length > 0 ? (
                <ReactApexChart
                  type="area"
                  options={trendOptions}
                  series={trendSeries}
                  height={200}
                  key={`trend-${trendDays}-${trend.length}`}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    color: "var(--color-text-secondary)",
                    fontSize: 13,
                  }}
                >
                  No data for this period
                </div>
              )}
            </DashboardWidget>
          )}

          {on("breakdown") && (
            <DashboardWidget
              title="Job breakdown"
              subtitle={`By type · this week · ${bkTotal} total`}
              accent="#854F0B"
            >
              {bkValues.length > 0 && bkValues.some((v) => v > 0) ? (
                <ReactApexChart
                  type="pie"
                  options={bkOptions}
                  series={bkValues}
                  height={240}
                  key={`pie-${bkValues.join("-")}`}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    color: "var(--color-text-secondary)",
                    fontSize: 13,
                  }}
                >
                  No jobs this week
                </div>
              )}
            </DashboardWidget>
          )}
        </div>
      )}

      {/* ── Recent runs + Master suite health ─────────────── */}
      {(on("runs") || on("health")) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: "1.25rem",
          }}
        >
          {on("runs") && (
            <DashboardWidget
              title="Recent runs"
              subtitle="Last 8 jobs across all types"
              loading={ldRuns}
              accent="#3B6D11"
              actions={
                <a
                  style={{ fontSize: 12, color: C.blue, cursor: "pointer" }}
                  onClick={() => navigate("/jobs")}
                >
                  View all <ArrowRightOutlined style={{ fontSize: 10 }} />
                </a>
              }
            >
              {runs.length === 0 && !ldRuns ? (
                <div
                  style={{
                    color: "var(--color-text-secondary)",
                    fontSize: 13,
                    padding: "20px 0",
                    textAlign: "center",
                  }}
                >
                  No recent runs
                </div>
              ) : (
                runs.map((run, i) => {
                  const clean = (v) => (v && v !== "N/A" ? v : null);
                  const { color, bg, icon } = catMeta(
                    clean(run.task_category) || clean(run.task_type),
                    run.task_sub_category,
                  );
                  const typeLabel =
                    clean(run.task_sub_category) ||
                    clean(run.task_category) ||
                    clean(run.task_type) ||
                    "—";
                  const jobName =
                    clean(run.test_suite) ||
                    clean(run.input_name) ||
                    run.result_summary_id ||
                    "—";
                  const stepTypes = clean(run.step_types);
                  const subtitle = `${typeLabel}${
                    stepTypes ? ` · ${stepTypes}` : ""
                  } · ${run.run_start || "—"}`;
                  const status = run.test_result || run.job_status || "—";
                  return (
                    <div
                      key={i}
                      onClick={() =>
                        run.result_summary_id &&
                        navigate("/results/details", {
                          state: { key: run.result_summary_id },
                        })
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 0",
                        borderBottom:
                          i < runs.length - 1
                            ? "0.5px solid var(--color-border-tertiary)"
                            : "none",
                        cursor: run.result_summary_id ? "pointer" : "default",
                      }}
                    >
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 7,
                          background: bg,
                          color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Tooltip title={jobName}>{jobName}</Tooltip>
                        </div>
                        <Tooltip title={subtitle}>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--color-text-secondary)",
                              marginTop: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {subtitle}
                          </div>
                        </Tooltip>
                      </div>
                      <RunStatusBadge status={status} size="sm" />
                    </div>
                  );
                })
              )}
            </DashboardWidget>
          )}

          {on("health") &&
            (() => {
              const filteredHealth =
                msFilter === "all"
                  ? health
                  : health.filter((ms) => msStatus(ms) === msFilter);

              // Click → live run if in progress, else the results report,
              // else (never run) fall back to the library.
              const openSuite = (ms) => {
                if (ms.last_run_status === "In Progress" && ms.last_run_job_id)
                  navigate("/jobs/details", {
                    state: { jobId: ms.last_run_job_id },
                  });
                else if (ms.last_run_job_id)
                  navigate("/results/mastersuite", {
                    state: { key: ms.last_run_job_id },
                  });
                else navigate("/mastersuite");
              };

              return (
                <DashboardWidget
                  title="Master suite health"
                  subtitle="Pass rate per suite · last run"
                  loading={ldHlth}
                  accent="#534AB7"
                  actions={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <PeriodTabs
                        value={msFilter}
                        onChange={setMsFilter}
                        options={MS_FILTERS}
                      />
                      <a
                        style={{
                          fontSize: 12,
                          color: C.blue,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => navigate("/mastersuite")}
                      >
                        View all <ArrowRightOutlined style={{ fontSize: 10 }} />
                      </a>
                    </div>
                  }
                >
                  {filteredHealth.length === 0 && !ldHlth ? (
                    <div
                      style={{
                        color: "var(--color-text-secondary)",
                        fontSize: 13,
                        padding: "20px 0",
                        textAlign: "center",
                      }}
                    >
                      {health.length === 0
                        ? "No master suites found"
                        : `No suites ${
                            MS_FILTERS.find((f) => f.value === msFilter)?.label.toLowerCase() ||
                            ""
                          }`}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2,1fr)",
                        gap: 10,
                        maxHeight: 340,
                        overflowY: "auto",
                        paddingRight: 2,
                      }}
                    >
                      {filteredHealth.map((ms, i) => (
                        <MsHealthCard
                          key={i}
                          ms={ms}
                          onClick={() => openSuite(ms)}
                        />
                      ))}
                    </div>
                  )}
                </DashboardWidget>
              );
            })()}
        </div>
      )}

      {/* ── Library snapshot ───────────────────────────────── */}
      {on("library") && (
        <div style={{ marginBottom: "1.25rem" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: ".6px",
              color: "var(--color-text-secondary)",
              marginBottom: ".75rem",
            }}
          >
            Library snapshot
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 12,
            }}
          >
            {[
              {
                label: "Test suites",
                key: "testSuites",
                color: C.blue,
                icon: <FileOutlined />,
                bg: C.blueBg,
                route: "/libraries/suites",
              },
              {
                label: "Tasks",
                key: "tasks",
                color: C.amber,
                icon: <CheckSquareOutlined />,
                bg: C.amberBg,
                route: "/libraries/task",
              },
              {
                label: "Master suites",
                key: "masterSuites",
                color: C.pass,
                icon: <DatabaseOutlined />,
                bg: C.passBg,
                route: "/mastersuite",
              },
              {
                label: "UI suites",
                key: "uiSuites",
                color: C.purple,
                icon: <DesktopOutlined />,
                bg: C.purpleBg,
                route: "/ui-automation",
              },
            ].map(({ label, key, color, icon, bg, route }) => (
              <div
                key={key}
                onClick={() => navigate(route)}
                style={{
                  background: "var(--color-background-primary)",
                  border: "0.5px solid var(--color-border-tertiary)",
                  borderTop: `3px solid ${color}`,
                  borderRadius: 12,
                  padding: "16px",
                  cursor: "pointer",
                  boxShadow:
                    "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
                  transition: "box-shadow .15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 4px 20px rgba(0,0,0,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)")
                }
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: bg,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 500,
                      color,
                      lineHeight: 1.1,
                    }}
                  >
                    {library[key] ?? "—"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--color-text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    {label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Failure heatmap ────────────────────────────────── */}
      {on("heatmap") && (
        <div style={{ marginBottom: "1.25rem" }}>
          <DashboardWidget
            title="Failure heatmap"
            subtitle="Failures per day · last 28 days"
            accent="#A32D2D"
          >
            <div>
              {/* Day labels */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7,1fr)",
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div
                    key={d}
                    style={{
                      fontSize: 10,
                      color: "var(--color-text-secondary)",
                      textAlign: "center",
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7,1fr)",
                  gap: 4,
                }}
              >
                {Array.from({ length: 28 }).map((_, i) => {
                  const d = heatmap[i];
                  const failures = d ? Number(d.failures) : 0;
                  const total = d ? Number(d.total) : 0;
                  return (
                    <Tooltip
                      key={i}
                      title={
                        d
                          ? `${d.run_date}: ${failures} failure${failures !== 1 ? "s" : ""} of ${total} jobs`
                          : "No data"
                      }
                    >
                      <div
                        style={{
                          height: 22,
                          borderRadius: 4,
                          background: heatColor(failures),
                          border: "0.5px solid var(--color-border-tertiary)",
                          cursor: "default",
                          transition: "transform .1s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "scale(1.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      />
                    </Tooltip>
                  );
                })}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 10,
                  alignItems: "center",
                  fontSize: 11,
                  color: "var(--color-text-secondary)",
                }}
              >
                <span>No failures</span>
                {[
                  "var(--color-background-secondary)",
                  "#C0DD97",
                  "#97C459",
                  "#639922",
                  "#3B6D11",
                ].map((c, i) => (
                  <div
                    key={i}
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: c,
                      border: "0.5px solid var(--color-border-tertiary)",
                    }}
                  />
                ))}
                <span>Many failures</span>
              </div>
            </div>
          </DashboardWidget>
        </div>
      )}

      {/* ── Coming soon ────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          margin: "1.5rem 0 1rem",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "0.5px",
            background: "var(--color-border-tertiary)",
          }}
        />
        <span
          style={{
            fontSize: 11,
            color: "var(--color-text-secondary)",
            whiteSpace: "nowrap",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: ".5px",
          }}
        >
          Coming soon
        </span>
        <div
          style={{
            flex: 1,
            height: "0.5px",
            background: "var(--color-border-tertiary)",
          }}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 12,
          marginBottom: "1.5rem",
        }}
      >
        {[
          {
            id: "sql",
            icon: <RobotOutlined />,
            color: C.purple,
            bg: C.purpleBg,
            title: "SQL Agent",
            desc: "Ask questions in plain English — ARC queries your databases and returns results instantly",
          },
          {
            id: "brd",
            icon: <FileTextOutlined />,
            color: C.blue,
            bg: C.blueBg,
            title: "BRD Generator",
            desc: "Auto-generate business requirement documents from your test specs and historical results",
          },
          {
            id: "agents",
            icon: <DesktopOutlined />,
            color: C.pass,
            bg: C.passBg,
            title: "Local agents",
            desc: "Run UI automation tests on client machines — no EC2 server or admin rights required",
          },
        ].map(
          ({ id, icon, color, bg, title, desc }) =>
            on(id) && (
              <div
                key={id}
                style={{
                  background: "var(--color-background-primary)",
                  border: "1px dashed var(--color-border-tertiary)",
                  borderRadius: 12,
                  padding: "1.25rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  opacity: 0.7,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: bg,
                      color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
                    <span
                      style={{
                        fontSize: 10,
                        padding: "1px 7px",
                        background: C.purpleBg,
                        color: C.purple,
                        borderRadius: 20,
                        fontWeight: 500,
                      }}
                    >
                      Coming soon
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--color-text-secondary)",
                    lineHeight: 1.5,
                  }}
                >
                  {desc}
                </div>
              </div>
            ),
        )}
      </div>

      {/* ── Customise drawer ───────────────────────────────── */}
      <Drawer
        title="Customise your dashboard"
        placement="right"
        width={360}
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setConfigOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              style={{ background: "#0B1F3A", borderColor: "#0B1F3A" }}
              onClick={saveConfig}
            >
              Save layout
            </Button>
          </div>
        }
      >
        <div
          style={{
            fontSize: 13,
            color: "var(--color-text-secondary)",
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          Toggle widgets on or off. Layout is saved per user — other users keep
          their own configuration.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {widgets.map((w) => (
            <div
              key={w.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                background: w.enabled
                  ? "var(--color-background-secondary)"
                  : "transparent",
                border: "0.5px solid var(--color-border-tertiary)",
                borderRadius: 8,
                transition: "background .15s",
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, fontWeight: w.enabled ? 500 : 400 }}
                >
                  {w.label}
                </div>
              </div>
              <Switch
                size="small"
                checked={w.enabled}
                onChange={(val) =>
                  setWidgets((prev) =>
                    prev.map((x) =>
                      x.id === w.id ? { ...x, enabled: val } : x,
                    ),
                  )
                }
              />
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 20,
            padding: "12px 14px",
            background: "var(--color-background-secondary)",
            borderRadius: 8,
            fontSize: 12,
            color: "var(--color-text-secondary)",
          }}
        >
          <DashboardOutlined style={{ marginRight: 6 }} />
          Drag-to-reorder coming in the next update
        </div>
      </Drawer>
    </div>
  );
}

export default Dashboard;
